import Context from "shared/util/Context";
import UrlHelpers from "shared/util/UrlHelpers";
import { scale } from "shared/util/NumberHelpers";

import {
	FILTERS,
	IMPORTANT_HEADER_TRIGGER,
} from "apps/profile_questions/util/constants";

/**
 * Get a username/userid from a profile url.
 *
 * @param {string} _url - the url
 * @return {string} - the username or userid found in the url, null if not found
 */
export const getUserFromUrlString = (_url) => {
	// y'all gotta pass the url
	if (typeof _url !== "string") {
		return null;
	}

	const url = _url.replace(/^\//, "");
	const defaultResponse = Context.get("userid");

	// if there are no slashes, this isn't a url (or at least a url that we want
	// to mess with)
	if (url.indexOf("/") < 0) {
		return defaultResponse;
	}

	const pieces = url.split("/");

	// if we aren't on profile, we can't get a username... so this means we are
	// on the `/questions` page, which is actually self-view
	if (pieces[0] !== "profile") {
		return defaultResponse;
	}

	return pieces[1];
};

/**
 * Programatically but deterministically get a placeholder width, using the
 * length of the question as a key.
 *
 * @param {string} text - the question text
 * @return {int} - a number to use as a placeholder width
 */
export const getPlaceholderWidth = (text = "") => {
	return scale({
		input: text.length,
		inMin: 40,
		inMax: 116,
		outMin: 30,
		outMax: 70,
	});
};

/**
 * Convert a number of seconds to a string of hours.
 *
 * @param {nubmer} seconds - the number of seconds to convert
 * @return {string} - the number of hours, e.g. "2hrs"
 */
export const secondsToHourString = (seconds) => {
	const hours = Math.round(seconds / 60 / 60);
	let suffix = hours === 1 ? "hr" : "hrs"; // no _str necessary; hours are hours

	if (hours === 0) {
		const min = Math.round(seconds / 60);
		suffix = "min";

		return `${min}${suffix}`;
	} else {
		return `${hours}${suffix}`;
	}
};

/**
 * As you answer more questions, your highest possible match percentage goes
 * up. This is the formula, passed down to us by our forebearers (aka copied
 * from the old bigdig service in the okcupid repo).
 *
 * @param {number} answerCount - the number of answers a user has
 * @return {number} - the maximum match percentage for that user
 */
export const getMaxPercentFromAnswerCount = (answerCount = 0) => {
	if (answerCount === 0) {
		return 0;
	}

	return Math.round((1 - 1.0 / answerCount) * 1000) / 10;
};

/**
 * Given a question object in selfview, figure out to which categories it
 * belongs.
 *
 * @param {Object} question - the question
 * @return {Array} - a list of categories into which the question falls
 */
export const getSelfviewQuestionCategories = (question) => {
	const res = [];

	if (!question || !question.target) {
		return res;
	}

	if (question.target.importance <= IMPORTANT_HEADER_TRIGGER) {
		res.push(FILTERS.IMPORTANT);
	}

	if (question.target.public) {
		res.push(FILTERS.PUBLIC);
	} else {
		res.push(FILTERS.PRIVATE);
	}

	if (question.target.note) {
		res.push(FILTERS.EXPLAINED);
	}

	if (question.target.skipped) {
		res.push(FILTERS.SKIPPED);
	}

	return res;
};

/**
 * Given a question list and a single question, return a new list with the
 * single question replaced with the new version.
 *
 * @param {Array} questions - a list of questions
 * @param {Object} updatedQuestion - the new question that you want added in
 * @return {Array} - the updated list of questions
 */
export const mapQuestionList = (questions, updatedQuestion) => {
	if (
		!questions ||
		!Array.isArray(questions) ||
		!updatedQuestion ||
		!(updatedQuestion.question && updatedQuestion.question.id)
	) {
		return questions;
	}

	return questions.map((question) => {
		if (updatedQuestion.question.id === question.question.id) {
			return updatedQuestion;
		} else {
			return question;
		}
	});
};

/**
 * Safely get the value of the filter_id url param.
 * @return {number|undefined} the value of the filter url param, if present
 */
export const getFilterUrlParam = () => {
	if (!window || !window.location) {
		return undefined;
	}

	const filterValues = {};

	Object.keys(FILTERS).forEach((filter) => {
		filterValues[FILTERS[filter]] = true;
	});

	const urlParam = UrlHelpers.getParam("filter_id");

	if (urlParam && urlParam.match(/^\d+$/) && filterValues[urlParam]) {
		return parseInt(urlParam, 10);
	} else {
		return undefined;
	}
};



// WEBPACK FOOTER //
// ./src/apps/profile_questions/util/helpers.js